import type { MonitoringResponse } from "@synthesis/shared-services/internal/monitoring/types";
import { FormProvider, useForm } from "react-hook-form";
import { RadioGroup } from "@synthesis/shared-ui";
import { useMemo } from "react";

import { MonitoringCard } from "./monitoring-card";
import { MonitoringSectionHeading } from "./monitoring-section-heading";
import { RailwayShareRow } from "./railway-share-row";
import {
  ALL_RAILWAYS_VALUE,
  getMonitoringRailwayShares,
  filterRailwayShares,
  MONITORING_SECTION_TOOLTIPS,
  splitRailwayShares,
  type RailwayScope,
} from "../monitoring-page.lib";

type RailwaysSectionProps = {
  data: MonitoringResponse;
  railwayId: string;
  railwayScope: RailwayScope;
  onRailwayScopeChange: (scope: RailwayScope) => void;
};

type RailwayScopeFormValues = {
  railwayScope: RailwayScope;
};

export const RailwaysSection = ({ data, railwayId, railwayScope, onRailwayScopeChange }: RailwaysSectionProps) => {
  const methods = useForm<RailwayScopeFormValues>({
    defaultValues: { railwayScope },
    values: { railwayScope },
  });

  const columns = useMemo(() => {
    const items = filterRailwayShares(getMonitoringRailwayShares(data, railwayScope), railwayId);
    return railwayId === ALL_RAILWAYS_VALUE ? splitRailwayShares(items) : [items];
  }, [data, railwayId, railwayScope]);

  return (
    <section className="flex flex-col gap-6">
      <MonitoringSectionHeading
        title="Дороги"
        tooltipTitle={MONITORING_SECTION_TOOLTIPS.railways.title}
        tooltipDescription={MONITORING_SECTION_TOOLTIPS.railways.description}
        actions={
          <FormProvider {...methods}>
            <RadioGroup
              name="railwayScope"
              horizontal
              gap="md"
              onClick={(value) => onRailwayScopeChange(value as RailwayScope)}
              renderRadioGroupElements={[
                {
                  id: "railway-scope-active",
                  value: "active",
                  label: "Активные заявки",
                  labelClassName: "subtitle-sm text-type-common",
                },
                {
                  id: "railway-scope-all",
                  value: "all",
                  label: "Все заявки",
                  labelClassName: "subtitle-sm text-type-common",
                },
              ]}
            />
          </FormProvider>
        }
      />

      <MonitoringCard title="Распределение заявок по железным дорогам">
        {columns.every((column) => column.length === 0) && <p className="paragraph-base text-type-minor">Нет данных по дорогам</p>}
        <div className="flex gap-[4.5rem]">
          {columns.map((column, columnIndex) => (
            <div key={columnIndex} className="flex min-w-0 flex-1 flex-col gap-6">
              {column.map((item) => (
                <RailwayShareRow key={item.id} item={item} scope={railwayScope} />
              ))}
            </div>
          ))}
        </div>
      </MonitoringCard>
    </section>
  );
};
